import {MediaFolder} from '../../reducers/media';
import {SimpleTable} from '../../ui-components/table/SimpleTable';
import {Button} from '../../ui-components/Button/button';
import * as React from 'react';
import {ReactComponent as FolderIcon} from 'src/assets/icons/folder.svg';
import {Row} from '@tanstack/react-table';
import {DeleteFolder} from './DeleteFolder';
import {StorageSourceCrud} from '../../api/org/storageSource/api';
import {TruncateTitle} from '../../utils/helper';
import {useEffect, useState} from 'react';
import {DeleteIconDisabled, Ellipses} from '../../ui-components/Iconpack';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../ui-components/Dropdown/dropdown';
import {ChevronRightIcon} from '@radix-ui/react-icons';
import {Dialog, DialogContent} from '../../ui-components/Modal';
import FolderShare from '../../components/folder-share';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import Tracker from '../../lib/analytics/tracker';
import {EventNames, EventProperty} from '../../lib/analytics/events';

interface FolderListProps {
    folders: MediaFolder[];
    onFolderClick: (folder: MediaFolder) => void;
    onFolderDelete: (folder: MediaFolder) => void;
    storageSourceCrud: StorageSourceCrud | undefined;
    forceRefresh: () => void;
}

export function FolderList(props: FolderListProps) {
    const [mdOrLarger, setMdOrLarger] = useState(false);
    const [shareFolder, setShareFolder] = useState(false);
    const [folderInfo, setFolderInfo] = useState<any>();
    const userProfileProps = useSelector((state: RootState) => state.userProfile);
    const isOriginalUser = userProfileProps.currentOrganization.uid === userProfileProps.my_organizations[0].uid;

    useEffect(() => {
        const handleResize = () => {
            setMdOrLarger(window.innerWidth >= 768);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return props.folders.length > 0 ? (
        <div>
            <SimpleTable
                className='w-full'
                columns={[
                    {
                        id: 'name',
                        header: 'Name',
                        cell: ({row}: {row: Row<MediaFolder>}) => {
                            return (
                                <div className={'flex w-full flex-row items-center'}>
                                    <FolderIcon className={'h-6 w-6 text-gray-600'} />
                                    <Button
                                        variant={'link'}
                                        onClick={() => {
                                            props.onFolderClick(row.original);
                                        }}>
                                        {mdOrLarger ? row.original.name : TruncateTitle(row.original.name, 12)}
                                    </Button>
                                </div>
                            );
                        },
                    },
                    {
                        id: 'actions',
                        // header: 'Actions',
                        cell: ({row}: {row: Row<MediaFolder>}) => {
                            const userEmail = userProfileProps.email;
                            const access = row.original.shares.length
                                ? row.original.shares.find((item) => item.user_email === userEmail)?.access_level
                                : undefined;
                            let canEdit = false;
                            if (
                                access === 'WRITE' ||
                                userProfileProps.currentOrganization.uid === userProfileProps.my_organizations[0].uid
                            ) {
                                canEdit = true;
                            }

                            return (
                                <div className={'flex flex-row'}>
                                    <div className={'flex-end ml-auto'}>
                                        <div className='flex gap-2'>
                                            {canEdit ? (
                                                <DeleteFolder
                                                    folder={row.original}
                                                    onFolderDelete={props.onFolderDelete}
                                                    storageSourceCrud={props.storageSourceCrud}
                                                />
                                            ) : (
                                                <DeleteIconDisabled />
                                            )}

                                            <DropdownMenu>
                                                <DropdownMenuTrigger>
                                                    {isOriginalUser && <Ellipses />}
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className='mx-7'>
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            setShareFolder(true);
                                                            setFolderInfo(row.original);
                                                            Tracker.trackEvent(EventNames.FOLDER_SHARE_CLICKED, {
                                                                [EventProperty.FolderName]: row.original.name,
                                                            });
                                                        }}>
                                                        <span className='cursor-pointer pr-[80px] text-[1rem] font-[400]'>
                                                            Share
                                                        </span>
                                                        <ChevronRightIcon />
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>
                                </div>
                            );
                        },
                    },
                ]}
                data={props.folders}
            />
            <Dialog open={shareFolder}>
                <DialogContent
                    className='w-[647px]'
                    onClose={() => {
                        setShareFolder(false);
                        props.forceRefresh();
                    }}>
                    <FolderShare folderInfo={folderInfo} />
                </DialogContent>
            </Dialog>
        </div>
    ) : (
        <div className={'p-4 text-center text-gray-500'}>No folders found</div>
    );
}

export function FolderGrid(props: FolderListProps) {
    const [mdOrLarger, setMdOrLarger] = React.useState(false);

    useEffect(() => {
        const handleResize = () => {
            setMdOrLarger(window.innerWidth >= 768);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            {props.folders.length > 0 ? (
                <div className={'m-2 grid grid-cols-2 gap-4 md:m-4 md:grid-cols-3'}>
                    {props.folders.map((folder) => (
                        <div
                            key={folder.uid}
                            className={
                                'flex shrink-0 cursor-pointer flex-row items-center rounded-md bg-blue-50 p-2 hover:bg-blue-100 md:p-4'
                            }
                            onClick={() => props.onFolderClick(folder)}>
                            <div className={'h-8 w-8'}>
                                <FolderIcon className={'h-8 w-8 text-gray-600'} />
                            </div>
                            <span className={' truncate pl-3'} title={folder.name}>
                                {mdOrLarger ? folder.name : TruncateTitle(folder.name, 12)}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={'p-4 text-center text-gray-500'}>No folders found</div>
            )}
        </>
    );
}
